/*
 * sofsy-gallery-touch.js: who owns a touch on the phone product gallery, the browser or the carousel (storefront #76).
 *
 * The gallery is a Splide slider (assets/featured-collection-script.js, imported by assets/bundle.js
 * mountUnifiedGallery, which leaves the live instance on the gallery element as `sofsySplide`).
 *
 * What was wrong (both pre-date Round 6; the gallery code is byte-identical before and after PR #62):
 *  1. Pinch changed the photo instead of zooming. Splide starts a drag on the first finger and follows
 *     changedTouches[0], so the second finger of a pinch kept dragging the slide, Splide called preventDefault on
 *     every two-finger touchmove (which stops Safari zooming), and on release it threw the slide by the first
 *     finger's speed. The old guard in bundle.js called Drag.disable(), but Splide only reads that flag when a
 *     touch STARTS, so a drag already under way carried on.
 *  2. Swipes were uneven. Splide measures the release speed only since its last 200 ms checkpoint, so the same
 *     150 px swipe turned the page at 140 ms and 260 ms but snapped back at 200 ms, depending on where the
 *     checkpoint fell. A swipe Splide judged slow had to cover half the photo.
 *
 * What this does, from document capture listeners, so it runs before Splide (bound on the track) sees anything:
 *  - A second finger, or any touch while the page is zoomed in, belongs to the browser: Splide never sees the rest
 *    of that touch, so nothing calls preventDefault and Safari zooms or pans natively. If Splide had already begun
 *    dragging, the slide is eased back to where it was, and the final touchend is let through with no throw, so
 *    Splide closes its drag cleanly on the same photo.
 *  - While zoomed in, the gallery takes touch-action:auto (sofsy-pdp.css), so a one-finger pan moves the zoomed
 *    page in every direction instead of doing nothing.
 *  - After Splide has handled a one-finger release on the phone's sideways slider (splide--ltr), a swipe of at least 12%
 *    of the gallery's width (never under 40 px), mostly sideways, that Splide left on the same photo turns one photo in
 *    that direction when either
 *      - it was quick (under QUICK_MS), or
 *      - Splide's own rule says it should have: Splide lands the slide at drag + flickPower x release speed and turns
 *        when that passes half a photo, but it reads the speed only since its last 200 ms checkpoint, so it often reads
 *        0 (audit of PR #77: 3 of 22 swipes of 300 ms or more snapped back). Here the speed is measured over the last
 *        SPEED_WINDOW_MS of the finger's own passive touchmoves, so the same swipe always gets the same answer.
 *    Splide's own decisions are never undone, and it never moves more than one photo. The go() relies on Splide not
 *    waiting for transitions (getOptions in bundle.js sets no waitForTransition, so Controller.isBusy() is always
 *    false): if anyone adds waitForTransition:true there, this go() would land during Splide's snap-back and be dropped.
 *
 * Nothing here calls preventDefault, so every listener is passive. Bound once on document: survives colour swaps,
 * which replace the gallery element. A touch that starts on an arrow, a dot, a link or a button is never turned
 * by the quick-swipe rule. The hand-off to the browser covers every layout, including the vertical slider (splide--ttb)
 * that an iPad in landscape gets at 989 px and wider (audit of PR #77, M1); the swipe rules are for splide--ltr only.
 */
(() => {
  if (window.SofsyGalleryTouch) return;

  const GALLERY = '.product-gallery.splide'; // every layout: the hand-off to the browser applies to the vertical slider too
  const ZOOMED_CLASS = 'sofsy-gallery-zoomed';
  const QUICK_MS = 300;
  const QUICK_MIN_PX = 40;
  const QUICK_SHARE = 0.12; // of the gallery's width: 47 px on a 393 px iPhone
  const NO_THROW = 1e-9; // Splide reads `flickPower || 600`, so 0 would mean 600
  const SPEED_WINDOW_MS = 100; // the release speed is the finger's speed over its last 100 ms

  // the touch in progress: { gallery, native, track, id, x, y, t, index, samples: [[timeStamp, clientX], ...] }
  let gesture = null;

  const zoomed = () => !!(window.visualViewport && window.visualViewport.scale > 1.01);
  const splideOf = (gallery) => (gallery && gallery.sofsySplide) || null;
  const galleryOf = (event) => {
    const target = event.target;
    return target && target.closest ? target.closest(GALLERY) : null;
  };

  function syncZoomClass() {
    const on = zoomed();
    document.querySelectorAll(GALLERY).forEach((el) => el.classList.toggle(ZOOMED_CLASS, on));
  }

  // The browser takes over this touch. If Splide is mid-drag, ease the slide back to where it was.
  function handToBrowser(gallery) {
    if (!gesture || gesture.gallery !== gallery) gesture = { gallery, native: true };
    if (gesture.native) return;
    gesture.native = true;
    const splide = splideOf(gallery);
    try {
      if (splide && splide.Components.Drag.isDragging()) splide.Components.Controller.go(splide.index, true);
    } catch (_) {
      /* the photo stays where it is; the browser still gets the gesture */
    }
  }

  function onStart(event) {
    const fingers = event.touches ? event.touches.length : 1;
    // A further finger on a carousel gesture, wherever it lands (on the title, the price): the browser's from here on.
    if (fingers > 1 && gesture && !gesture.native) {
      const g = gesture.gallery;
      handToBrowser(g);
      if (g.contains(event.target)) event.stopPropagation();
      return;
    }
    const gallery = galleryOf(event);
    if (!gallery) return;

    // A further finger on a gesture already under way: from here on it is the browser's.
    if (fingers > 1 && gesture && gesture.gallery === gallery) {
      handToBrowser(gallery);
      event.stopPropagation();
      return;
    }
    // Two fingers at once, or any touch while zoomed in: the browser's from the start; Splide never sees it.
    if (fingers > 1 || zoomed()) {
      gesture = { gallery, native: true };
      event.stopPropagation();
      return;
    }

    // One finger: a fresh gesture (whatever an earlier one left behind is dropped). Splide drags it as usual.
    const touch = event.changedTouches && event.changedTouches[0];
    const splide = splideOf(gallery);
    const onControl = event.target.closest('button, a, .splide__arrows, .splide__pagination');
    gesture = {
      gallery,
      native: false,
      track: !onControl,
      id: touch ? touch.identifier : null,
      x: touch ? touch.clientX : 0,
      y: touch ? touch.clientY : 0,
      t: event.timeStamp,
      index: splide ? splide.index : -1,
      samples: touch ? [[event.timeStamp, touch.clientX]] : [],
    };
  }

  // The carousel's finger: remember where it was and when, for its release speed. Only the last ~300 ms is kept.
  function sample(event) {
    if (!gesture.track) return;
    const list = event.changedTouches || [];
    for (let k = 0; k < list.length; k += 1) {
      if (list[k].identifier !== gesture.id) continue;
      const s = gesture.samples;
      s.push([event.timeStamp, list[k].clientX]);
      while (s.length > 2 && event.timeStamp - s[1][0] > 3 * SPEED_WINDOW_MS) s.shift();
      return;
    }
  }

  // px per ms, signed, over the last SPEED_WINDOW_MS before the release at (t, x). 0 if the finger was held still.
  function releaseSpeed(g, t, x) {
    let ref = g.samples[0] || [g.t, g.x];
    for (const s of g.samples) {
      if (s[0] <= t - SPEED_WINDOW_MS) ref = s;
      else break;
    }
    const dt = t - ref[0];
    return dt > 0 ? (x - ref[1]) / dt : 0;
  }

  function onMove(event) {
    if (!gesture) return;
    if (!gesture.native) {
      sample(event);
      return;
    }
    if (!gesture.gallery.contains(event.target)) return;
    event.stopPropagation();
  }

  function onEnd(event) {
    if (!gesture || !gesture.native) return;
    const left = event.touches ? event.touches.length : 0;
    if (left > 0) {
      if (gesture.gallery.contains(event.target)) event.stopPropagation();
      return;
    }
    // Last finger up. Splide may still hold a drag from the first finger: let this one event through so it closes
    // it, with no throw, so it settles on the nearest photo (the one it was eased back to).
    const splide = splideOf(gesture.gallery);
    gesture = null; // so the bubble-phase afterEnd leaves this release alone
    if (!splide) return;
    const options = splide.options;
    const own = Object.prototype.hasOwnProperty.call(options, 'flickPower');
    const saved = options.flickPower;
    options.flickPower = NO_THROW;
    window.setTimeout(() => {
      if (own) options.flickPower = saved;
      else delete options.flickPower;
    }, 0);
  }

  // Bubble phase on document: Splide has already handled this release.
  function afterEnd(event) {
    const g = gesture;
    if (!g || g.native || (event.touches && event.touches.length)) return;
    gesture = null;
    if (!g.track || !g.gallery.contains(event.target) || event.type !== 'touchend') return;
    const touch = event.changedTouches && event.changedTouches[0];
    const splide = splideOf(g.gallery);
    if (!touch || !splide || splide.root !== g.gallery) return;
    if (!g.gallery.classList.contains('splide--ltr')) return; // the quick-swipe rule is for the phone's sideways slider only
    const dx = touch.clientX - g.x;
    const dy = touch.clientY - g.y;
    const width = g.gallery.clientWidth;
    const far = Math.max(QUICK_MIN_PX, QUICK_SHARE * width);
    if (Math.abs(dx) < far || Math.abs(dx) <= Math.abs(dy)) return;
    if (splide.index !== g.index) return; // Splide turned the photo itself
    const quick = event.timeStamp - g.t < QUICK_MS;
    // Splide's rule with a reliable speed: where the slide would land, drag plus flickPower x release speed.
    const land = dx + releaseSpeed(g, event.timeStamp, touch.clientX) * (splide.options.flickPower || 600);
    const fast = Math.sign(land) === Math.sign(dx) && Math.abs(land) >= width / 2;
    if (!quick && !fast) return;
    try {
      splide.go(dx < 0 ? '>' : '<');
    } catch (_) {
      /* Splide settles on the photo it chose */
    }
  }

  const passive = { passive: true, capture: true };
  document.addEventListener('touchstart', onStart, passive);
  document.addEventListener('touchmove', onMove, passive);
  document.addEventListener('touchend', onEnd, passive);
  document.addEventListener('touchcancel', onEnd, passive);
  document.addEventListener('touchend', afterEnd, { passive: true });
  document.addEventListener('touchcancel', afterEnd, { passive: true });

  if (window.visualViewport) window.visualViewport.addEventListener('resize', syncZoomClass, { passive: true });
  document.addEventListener('sectionReloadFinished', () => window.setTimeout(syncZoomClass, 0));

  window.SofsyGalleryTouch = Object.freeze({ QUICK_MS, QUICK_MIN_PX, QUICK_SHARE, SPEED_WINDOW_MS });
})();
